import { Database, Star, ChevronRight, TrendingUp, Recycle, Wrench, FileSpreadsheet, Boxes, PackageSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsSuperadmin } from "@/hooks/useUserRole";



export default function DataManagement() {
  const { t } = useTranslation("admin");
  const { isLoading } = useIsSuperadmin();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Database className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("data.title")}</h1>
        </div>
        <p className="text-muted-foreground">{t("data.subtitle")}</p>
      </div>

      <div className="px-6 space-y-6">

        {/* Appliance Categories */}
        <button
          onClick={() => navigate("/platform/data/appliance-categories")}
          className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Boxes className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">{t("settings.sections.applianceCategories.title")}</p>
            <p className="text-sm text-muted-foreground">{t("settings.sections.applianceCategories.description")}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Recommendation Engine */}
        <button
          onClick={() => navigate("/platform/data/recommendation-engine")}
          className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">Recommendation Engine</p>
            <p className="text-sm text-muted-foreground">Upload and activate the harvest recommendation sheet.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Spare Part Configuration */}
        <button
          onClick={() => navigate("/platform/data/spare-parts")}
          className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Wrench className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">{t("data.tiles.spareParts.title")}</p>
            <p className="text-sm text-muted-foreground">{t("data.tiles.spareParts.description")}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {[
          { to: "/platform/data/brand-harvest-defaults", title: t("settings:dataHub.brandHarvestTitle"), desc: t("settings:dataHub.brandHarvestDesc"), Icon: Recycle },
          { to: "/platform/data/legacy-parts", title: t("legacyParts.title"), desc: t("legacyParts.description"), Icon: PackageSearch },
          { to: "/platform/data/grade-pricing", title: t("settings:dataHub.gradePricingTitle"), desc: t("settings:dataHub.gradePricingDesc"), Icon: Star },
        ].map((c) => (
          <button
            key={c.to}
            onClick={() => navigate(c.to)}
            className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <c.Icon className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">{c.title}</p>
              <p className="text-sm text-muted-foreground">{c.desc}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        ))}

        {[
          { to: "/platform/data/appliance-prices", key: "appliancePrices" },
          { to: "/platform/data/refurbished-prices", key: "refurbPrices" },
        ].map((c) => (
          <button
            key={c.key}
            onClick={() => navigate(c.to)}
            className="w-full bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">{t(`data.tiles.${c.key}.title`)}</p>
              <p className="text-sm text-muted-foreground">{t(`data.tiles.${c.key}.description`)}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        ))}


      </div>

    </div>
  );
}
